import React, { useEffect } from "react";
import "./css/PrivacyPolicy.css";

import Navbar from "./components/Navbar";
import Newsletter from "./components/NewsLetter";
import Footer from "./components/Footer";

function RefundPolicy() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="page-wrapper">

            <div className="privacy-policy-page">

                <div className="container py-5">
                    <div className="row justify-content-center">

                        {/* Site Navigation */}
                        <Navbar />

                        <div className="col-lg-10">

                            {/* Page Header */}
                            <div className="privacy-header text-center mb-5">
                                <h1 className="privacy-title">
                                    Refund Policy
                                </h1>

                                <p className="privacy-subtitle">
                                    Fixovera Pvt Ltd — Household Services Digital Marketplace
                                </p>

                                <p className="mt-2 text-muted">
                                    Version 2.0 | Effective Date: 9 July 2026
                                </p>
                            </div>

                            {/* Policy Content Card */}
                            <div className="privacy-card p-4 p-md-5 shadow-sm">

                                {/* Introduction */}
                                <h3>Introduction</h3>

                                <p className="lead-text">
                                    This Refund Policy ("Policy") explains the terms and
                                    conditions under which <strong>Fixovera Pvt Ltd</strong>
                                    processes refunds for household services booked through
                                    the Fixovera mobile application and website.
                                </p>

                                <p>
                                    The policy applies to all eligible bookings and payment
                                    methods supported on the Platform and should be read
                                    together with our Terms of Service and other applicable
                                    policies.
                                </p>

                                <hr className="section-divider" />

                                {/* 1. Purpose */}
                                <h3>1. Purpose</h3>

                                <p>
                                    This Refund Policy ("Policy") governs the terms under
                                    which <strong>Fixovera Pvt Ltd</strong> ("Fixovera",
                                    "we", "us", or "our") processes refunds to customers
                                    who book household services, including but not limited
                                    to cleaning, plumbing, electrical work, appliance
                                    repair, pest control, painting, and carpentry through
                                    the Fixovera mobile application and website
                                    (collectively, the "Platform").
                                </p>

                                <p>
                                    This Policy applies to all bookings made through the
                                    Platform, whether fulfilled by Fixovera-affiliated
                                    professionals or by independent third-party service
                                    partners listed on the Platform.
                                </p>

                                <hr className="section-divider" />

                                {/* 2. Scope */}
                                <h3>2. Scope and Applicability</h3>

                                <ul>
                                    <li>
                                        This Policy applies to all customers who make a
                                        payment through the Platform using any supported
                                        payment method, including cards, UPI, net banking,
                                        wallets, or Fixovera credits.
                                    </li>

                                    <li>
                                        This Policy applies uniformly across all service
                                        categories listed on the Platform unless a
                                        category-specific policy is expressly published
                                        and linked on the relevant service page.
                                    </li>

                                    <li>
                                        Where a service is booked as part of a subscription
                                        or package, refunds are governed by
                                        <strong> Section 8 (Subscriptions and Packages)</strong>
                                        in addition to the general provisions below.
                                    </li>
                                </ul>

                                <hr className="section-divider" />

                                {/* 3. General Refund Principles */}
                                <h3>3. General Refund Principles</h3>

                                <ol>
                                    <li>
                                        Refunds are assessed based on the timing of
                                        cancellation, the stage of service completion,
                                        and the reason provided at the time of the refund
                                        request.
                                    </li>

                                    <li>
                                        All refund requests must be raised through the
                                        Platform's <strong>"Help & Support"</strong>
                                        section or by contacting Fixovera customer
                                        support within <strong>7 days</strong> of the
                                        scheduled or completed service date.
                                    </li>

                                    <li>
                                        Refunds, where approved, are issued to the
                                        original payment method used at the time of
                                        booking. Refunds to <strong>Fixovera Wallet</strong>
                                        may be offered as an alternative and, where
                                        selected by the customer, are processed faster
                                        than refunds to the original payment method.
                                    </li>

                                    <li>
                                        Convenience fees, platform fees, and payment
                                        gateway charges, where separately disclosed at
                                        checkout, are non-refundable unless the
                                        cancellation is due to an error attributable
                                        to Fixovera.
                                    </li>
                                </ol>

                                <hr className="section-divider" />

                                {/* 4. Cancellation Based Refunds */}
                                <h3>4. Cancellation-Based Refunds</h3>

                                <p>
                                    The following table summarizes standard refund
                                    eligibility based on cancellation timing relative
                                    to the scheduled service slot.
                                </p>

                                <div className="table-responsive my-4">
                                    <table className="table table-bordered align-middle">
                                        <thead>
                                            <tr>
                                                <th>Scenario</th>
                                                <th>Eligibility</th>
                                                <th>Refund</th>
                                                <th>Processing Time</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            <tr>
                                                <td>Service provider cancels / no-show</td>
                                                <td>Automatic</td>
                                                <td>100%</td>
                                                <td>2–4 business days</td>
                                            </tr>

                                            <tr>
                                                <td>Customer cancels (24+ hrs before slot)</td>
                                                <td>Automatic</td>
                                                <td>100%</td>
                                                <td>3–5 business days</td>
                                            </tr>

                                            <tr>
                                                <td>Customer cancels (2–24 hrs before slot)</td>
                                                <td>Automatic</td>
                                                <td>50%</td>
                                                <td>3–5 business days</td>
                                            </tr>

                                            <tr>
                                                <td>Customer cancels (&lt; 2 hrs before slot)</td>
                                                <td>Not Eligible</td>
                                                <td>0%</td>
                                                <td>—</td>
                                            </tr>

                                            <tr>
                                                <td>Service not completed / partially completed</td>
                                                <td>Case Review</td>
                                                <td>Up to 100%</td>
                                                <td>5–7 business days</td>
                                            </tr>

                                            <tr>
                                                <td>Verified service quality complaint</td>
                                                <td>Case Review</td>
                                                <td>50–100%</td>
                                                <td>5–7 business days</td>
                                            </tr>

                                            <tr>
                                                <td>Duplicate or erroneous payment</td>
                                                <td>Automatic</td>
                                                <td>100%</td>
                                                <td>2–4 business days</td>
                                            </tr>

                                            <tr>
                                                <td>Customer unavailable at confirmed slot</td>
                                                <td>Not Eligible</td>
                                                <td>0%</td>
                                                <td>—</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <p>
                                    <strong>Note:</strong> Time-based thresholds, such as
                                    24 hours and 2 hours, are measured against the
                                    originally scheduled slot at the time of booking
                                    confirmation, not any subsequently rescheduled time,
                                    unless the reschedule was initiated by Fixovera or
                                    the service partner.
                                </p>

                                <hr className="section-divider" />

                                {/* 5. Service Quality */}
                                <h3>5. Service Quality Issues</h3>

                                <ol>
                                    <li>
                                        The customer must report the issue within
                                        <strong> 48 hours</strong> of service completion
                                        through the Platform, including a description
                                        of the issue and, where applicable, supporting
                                        photographs or videos.
                                    </li>

                                    <li>
                                        Fixovera's Quality Assurance team will review the
                                        complaint and may, at its discretion, offer a
                                        free re-service, a partial refund, or a full
                                        refund based on the severity and verifiability
                                        of the issue.
                                    </li>

                                    <li>
                                        Fixovera reserves the right to request additional
                                        information or conduct a site inspection before
                                        finalizing a quality-related refund decision.
                                    </li>

                                    <li>
                                        Refunds for quality issues will not be granted
                                        where the customer has prevented the service
                                        partner from completing corrective work offered
                                        under this Policy.
                                    </li>
                                </ol>

                                <hr className="section-divider" />

                                {/* 6. Non Refundable */}
                                <h3>6. Non-Refundable Circumstances</h3>

                                <p>
                                    Refunds will generally <strong>not</strong> be
                                    provided in the following circumstances:
                                </p>

                                <ul>
                                    <li>
                                        Customer provides an incorrect address, contact
                                        details, or access instructions resulting in
                                        failed service delivery.
                                    </li>

                                    <li>
                                        Customer or an authorized occupant is unavailable
                                        at the confirmed service location at the
                                        scheduled time, and no rescheduling request is
                                        made in advance.
                                    </li>

                                    <li>
                                        Change of mind after the service partner has
                                        commenced work at the location.
                                    </li>

                                    <li>
                                        Additional work requested beyond the original
                                        scope, which was quoted and approved separately
                                        by the customer.
                                    </li>

                                    <li>
                                        Any damage, delay, or dissatisfaction arising
                                        from customer-supplied materials, parts, or
                                        equipment.
                                    </li>

                                    <li>
                                        Promotional bookings, discounted trial services,
                                        or zero-cost bookings, except where required
                                        by applicable law.
                                    </li>
                                </ul>

                                <hr className="section-divider" />

                                {/* 7. Processing */}
                                <h3>7. Refund Processing Timelines</h3>

                                <p>
                                    Approved refunds are initiated within
                                    <strong> 2 business days</strong> of approval.
                                </p>

                                <p>
                                    Depending on the customer's bank, card network, or
                                    payment provider, funds may take an additional
                                    <strong> 3–7 business days</strong> to reflect in
                                    the customer's account.
                                </p>

                                <p>
                                    Refunds to the <strong>Fixovera Wallet</strong> are
                                    typically credited within <strong>24 hours</strong>
                                    of approval.
                                </p>

                                <p>
                                    Fixovera is not responsible for delays caused by
                                    third-party banks or payment processors.
                                </p>

                                <hr className="section-divider" />

                                {/* 8. Subscriptions */}
                                <h3>8. Subscriptions and Packages</h3>

                                <ul>
                                    <li>
                                        Unused sessions in a prepaid package may be
                                        refunded on a <strong>pro-rata basis</strong>,
                                        less any discount applied for the bundled
                                        purchase, if cancellation is requested before
                                        <strong> 50%</strong> of the package has been
                                        utilized.
                                    </li>

                                    <li>
                                        No refunds will be issued for packages where
                                        more than <strong>50%</strong> of the sessions
                                        have been availed.
                                    </li>

                                    <li>
                                        Subscription plans may be cancelled at any time
                                        to stop future renewals; refunds for the current
                                        billing cycle are not provided once the cycle
                                        has commenced, except where required by
                                        applicable law.
                                    </li>
                                </ul>

                                <hr className="section-divider" />

                                {/* 9. Disputes */}
                                <h3>9. Disputes and Escalation</h3>

                                <ol>
                                    <li>
                                        If a customer disagrees with a refund decision,
                                        they may request an escalation through the
                                        Platform's support section within
                                        <strong> 7 days</strong> of the decision.
                                    </li>

                                    <li>
                                        Escalated cases are reviewed by a senior member
                                        of Fixovera's Customer Experience team, whose
                                        decision is final and binding, without prejudice
                                        to the customer's statutory rights.
                                    </li>

                                    <li>
                                        Fixovera aims to resolve all escalations within
                                        <strong> 10 business days</strong> of receipt.
                                    </li>
                                </ol>

                                <hr className="section-divider" />

                                {/* 10. Fraud */}
                                <h3>10. Fraud, Abuse, and Policy Misuse</h3>

                                <p>
                                    Fixovera reserves the right to deny refund requests,
                                    suspend accounts, or take other appropriate action
                                    where it reasonably determines that a refund request
                                    is fraudulent, abusive, or made in bad faith,
                                    including but not limited to:
                                </p>

                                <ul>
                                    <li>Repeated last-minute cancellations.</li>
                                    <li>False damage or service quality claims.</li>
                                    <li>Misuse of promotional credits.</li>
                                    <li>
                                        Any other activity that constitutes fraud, abuse,
                                        or misuse of the Refund Policy.
                                    </li>
                                </ul>

                                <hr className="section-divider" />

                                {/* 11. Contact */}
                                <h3>11. Contact Us</h3>

                                <p>
                                    For questions about this Policy or to raise a refund
                                    request, please contact us through:
                                </p>

                                <ul className="contact-details-list">
                                    <li>
                                        <span className="info-label">
                                            In-App:
                                        </span>
                                        <span className="info-value">
                                            Help &amp; Support &gt; Refunds &amp;
                                            Cancellations
                                        </span>
                                    </li>

                                    <li>
                                        <span className="info-label">
                                            Email:
                                        </span>
                                        <span className="info-value">
                                            <a href="mailto:support@fixovera.com">
                                                support@fixovera.com
                                            </a>
                                        </span>
                                    </li>

                                    <li>
                                        <span className="info-label">
                                            Phone:
                                        </span>
                                        <span className="info-value">
                                            <a href="tel:+916290424024">
                                                +91 62904 25024
                                            </a>
                                        </span>
                                    </li>
                                </ul>

                                <div className="mt-5 p-4 rounded-4 bg-light">
                                    <p className="mb-1">
                                        <strong>Fixovera Pvt Ltd</strong>
                                    </p>
                                    <p className="mb-0 text-muted">
                                        Household Services Digital Marketplace
                                    </p>
                                    <p className="mb-0 text-muted">
                                        Refund Policy — Version 2.0
                                    </p>
                                    <p className="mb-0 text-muted">
                                        Effective Date: 9 July 2026
                                    </p>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Newsletter */}
            <Newsletter />

            {/* Footer */}
            <Footer />

        </div>
    );
}

export default RefundPolicy;